<!-- This is a page where a carrier signs an onboarding doc -->
<template>
  <div v-if="!mainLoading" class="uk-container uk-margin uk-flex uk-flex-column uk-flex-center">
    <h2>Onboarding for {{ onboarderCompany.name }}</h2>
    <section class="uk-section uk-section-primary">
      <h3>{{ displayDocTypeName(docType) }}</h3>
    </section>

    <div class="pdfContainer">
      <pdf
        v-for="i in numPages"
        :key="i"
        :src="pdfLoadingTask"
        :page="i"
        style="height: 100%"
      ></pdf>
    </div>

    <div>
      <a :href="docFileUrl" target="_blank">Download this document</a>
    </div>

    <!-- Signature area -->
    <div v-if="submitLoading">
      <div uk-spinner="ratio: 5" class="uk-padding"></div>
    </div>
    <template v-else>
      <div uk-grid class="uk-child-width-1-2@s uk-child-width-1-4@m uk-margin-bottom uk-text-left">
        <InputField label="Full Name" v-model="signerName" />
        <InputField label="Your Initials" v-model="signerInitials" />
        <InputField label="Your Title" v-model="signerTitle" />

        <InputField label="Date" v-model="date" />

        <InputField label="Company Name" v-model="carrierInfo.name" />
        <InputField label="MC #" v-model="carrierInfo.mcNum" />
        <InputField label="Phone #" v-model="carrierInfo.dispatchPhone" />
        <InputField label="Fax #" v-model="carrierInfo.faxNumber" />
      </div>
      <AddressInputBlock
        :address="carrierInfo.officeAddress"
        />

      <SignaturePad ref="signaturePad" />

      <div class="uk-flex uk-flex-center">
        <button
          class="uk-button uk-button-large uk-button-secondary uk-form-controls uk-margin-top"
          v-on:click="goBackToOnboardingPage"
        >
          <div>Go back</div>
        </button>
        <button
          class="uk-button uk-button-large uk-button-primary uk-form-controls uk-margin-top"
          v-on:click="submitSignature"
        >
          <div>Sign</div>
        </button>
      </div>
      <p class="uk-text-danger uk-text-large">{{ submitError }}</p>
      <br />
    </template>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
// import UIkit from 'uikit';

import pdf from 'vue-pdf-embed';

import AddressInputBlock from '../core/AddressInputBlock.vue';
import { displayDocTypeNameMixin } from '../../utils/onboarding_docs';
// import Button from '../core/Button.vue';
import InputField from '../core/Input/InputField.vue';

import SignaturePad from '../core/SignaturePad.vue';

import GET_COMPANY_DATA from '../../gql/company/CompanyWithDocuments.gql';
import { addressMixin } from '../../utils/address';

export default defineComponent({
  name: 'SignForm',
  components: {
    pdf,
    SignaturePad,
    InputField,
    AddressInputBlock,
  },
  props: {
    userCompanyId: Number,
    carrierCompanyId: Number,
    docType: String,

    // Has same fields as the same object in OnboardingForm
    carrierInfo: Object,
  },
  mixins: [displayDocTypeNameMixin, addressMixin],
  data() {
    return {
      pdfLoadingTask: null,
      docFileUrl: null,

      currentPage: 0,
      pageCount: 0,
      numPages: 0,

      mainLoading: true,
      submitLoading: false,
      submitError: null,

      signerName: null,
      signerInitials: null,
      signerTitle: null,
      date: null,
    };
  },
  methods: {
    pdfError(error) {
      console.error(error);
    },
    findDocFileUrl() {
      const vm = (this as any);

      const docs = vm.onboarderCompany.infoDocuments;
      const foundDoc = docs.find((d) => {
        if (d.docType === vm.docType) {
          return true;
        } else return false;
      });

      console.log('this doc file: ', foundDoc);

      return foundDoc.docFileUrl;
    },
    goBackToOnboardingPage() {
      (this as any).$emit('showMainOnboarding');
    },
    validateFields() {
      const vm = (this as any);

      if (!(vm.$refs.signaturePad as any).hasSignature()) {
        vm.submitError = 'Input signature';
        return false;
      }

      const allFieldsFilled =
        vm.signerName &&
        vm.signerInitials &&
        vm.signerTitle &&
        vm.date &&
        vm.carrierInfo.name &&
        vm.carrierInfo.officeAddress.streetAddress &&
        vm.carrierInfo.officeAddress.city &&
        vm.carrierInfo.officeAddress.state &&
        vm.carrierInfo.officeAddress.zipCode &&
        vm.carrierInfo.mcNum &&
        vm.carrierInfo.dispatchPhone &&
        vm.carrierInfo.faxNumber;

      if (!allFieldsFilled) {
        vm.submitError = 'Fill all required fields';
        return false;
      }

      return true;
    },

    async submitSignature() {
      const vm = (this as any);

      if (!vm.validateFields()) {
        return;
      } else {
        vm.submitError = null;
      }

      const signatureImage = await (vm.$refs.signaturePad as any).getSignatureAsImage();

      console.log('submitting img: ', signatureImage);

      // TODO in future: need to make the fields on this form configurable by broker so they can choose what fields they need overlaid on the doc
      //  - should be saved in DB so that on the backend route, we know what formData fields to receive
      const formData = new FormData();
      formData.append('userCompanyId', vm.userCompanyId.toString());
      formData.append('carrierCompanyId', vm.carrierCompanyId.toString());

      formData.append('documentType', vm.docType);
      formData.append('file', signatureImage);
      formData.append('imageExt', 'png');

      formData.append('signerName', vm.signerName);
      formData.append('signerInitials-pg1', vm.signerInitials);
      formData.append('signerInitials-pg2', vm.signerInitials);
      formData.append('signerInitials-pg3', vm.signerInitials);
      formData.append('signerInitials-pg4', vm.signerInitials);
      formData.append('signerInitials-pg5', vm.signerInitials);
      formData.append('signerTitle', vm.signerTitle);
      formData.append('date', vm.date);
      formData.append('companyName', vm.carrierInfo.name);
      formData.append('companyAddress', vm.fullAddress(vm.carrierInfo.officeAddress));
      formData.append('mcNum', vm.carrierInfo.mcNum);
      formData.append('phoneNum', vm.carrierInfo.dispatchPhone);
      formData.append('faxNum', vm.carrierInfo.faxNumber);

      vm.submitLoading = true;

      fetch('/api/onboarding/upload-document-signature', {
        method: 'POST',
        body: formData,
      })
        .then((response) => {
          if (!response.ok) {
            return response.json().then((errMsg) => {
              throw new Error(errMsg.detail);
            });
          }

          return response.json();
        })
        .then((data) => {
          console.log('uploaded signature!', data);
          vm.submitLoading = false;
          vm.submitError = null;

          vm.goBackToOnboardingPage();
        })
        .catch((error) => {
          console.error('Upload signature err: ', error);
          vm.submitLoading = false;
          vm.submitError = error;
        });
    },
  },
  apollo: {
    onboarderCompany: {
      query: GET_COMPANY_DATA,
      variables() {
        return {
          companyId: (this as any).userCompanyId,
        };
      },
      update: (data) => data.company,
      result(result) {
        const vm = (this as any);

        console.log('[fetched data] result: ', result);
        vm.mainLoading = false;
        vm.docFileUrl = vm.findDocFileUrl();
        vm.pdfLoadingTask = pdf.createLoadingTask(vm.docFileUrl);
        vm.pdfLoadingTask.promise.then((pdfRet) => {
          vm.numPages = pdfRet.numPages;
        });
      },
      error(error) {
        const vm = (this as any);

        vm.mainLoading = false;
        console.error('Fetching user data failed: ', error);
        vm.errorText = 'new error';
        console.log('err: ', vm.errorText);
      },
    },
  },
} as any);
</script>

<style lang="scss" scoped>
.large-button {
  height: 75px;
  font-size: 20px;
}

.pdfContainer {
  overflow-y: scroll;
  max-height: 800px;
  border-style: solid;
}

::-webkit-scrollbar {
  -webkit-appearance: none;
  width: 7px;
}

::-webkit-scrollbar-thumb {
  border-radius: 4px;
  background-color: rgba(0, 0, 0, 0.5);
  box-shadow: 0 0 1px rgba(255, 255, 255, 0.5);
}
</style>
